import React, { useMemo, useEffect, useState } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Typography, Box, Chip } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
    customStringOperators,
    stringToColor,
    getRowClassName,
    formatDate
} from './TabelaProcessada';

const TabelaPreview = () => {
    const [tableData, setTableData] = useState([]);

    useEffect(() => {
        const storedData = sessionStorage.getItem('previewTableData');
        if (storedData) {
            try {
                const parsed = JSON.parse(storedData);
                setTableData(parsed);
            } catch (e) {
                console.error('Erro ao ler dados do sessionStorage', e);
            }
        }
    }, []);

    const columns = useMemo(() => {
        const fixedColumns = [
            { field: 'Data', headerName: 'Data', width: 110, valueFormatter: (params) => formatDate(params.value) },
            {
                field: 'Débitos', headerName: 'Débitos', width: 130, type: 'number',
                renderCell: (params) => {
                    const val = parseFloat(params.value);
                    if (isNaN(val) || val === 0) return '-';
                    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
                }
            },
            {
                field: 'Créditos', headerName: 'Créditos', width: 130, type: 'number',
                renderCell: (params) => {
                    const val = parseFloat(params.value);
                    if (isNaN(val) || val === 0) return '-';
                    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
                }
            },
            { field: 'Registro', headerName: 'Registro', width: 100, filterOperators: customStringOperators },
            { field: 'Histórico', headerName: 'Histórico', width: 250, filterOperators: customStringOperators },
            {
                field: 'Conciliacao Histórico',
                headerName: 'Conciliação Histórico',
                width: 170,
                renderCell: (params) => {
                    const isOk = params.value === 'OK';
                    return (
                        <Box display="flex" alignItems="center" gap={0.5}>
                            {isOk ? <CheckCircleOutlineIcon color="success" fontSize="small" /> : <ErrorOutlineIcon color="error" fontSize="small" />}
                            <Typography variant="body2" color={isOk ? "success.main" : "error.main"} fontWeight={500}>
                                {params.value}
                            </Typography>
                        </Box>
                    );
                }
            },
            {
                field: 'Conciliacao Valor',
                headerName: 'Conciliação Valor',
                width: 170,
                renderCell: (params) => {
                    const isOk = params.value === 'OK';
                    return (
                        <Box display="flex" alignItems="center" gap={0.5}>
                            {isOk ? <CheckCircleOutlineIcon color="success" fontSize="small" /> : <ErrorOutlineIcon color="error" fontSize="small" />}
                            <Typography variant="body2" color={isOk ? "success.main" : "error.main"} fontWeight={500}>
                                {params.value}
                            </Typography>
                        </Box>
                    );
                }
            },
            {
                field: 'Observacao',
                headerName: 'Observação',
                width: 180,
                renderCell: (params) => (
                    params.value ? (
                        <Chip
                            label={params.value === 'Conciliado pela IA' ? '🤖 IA' : params.value}
                            color={params.value === 'Conciliado pela IA' ? 'info' : 'default'}
                            variant="filled"
                            size="small"
                        />
                    ) : null
                )
            },
            {
                field: 'Grupo Conciliacao',
                headerName: 'ID Conciliação',
                width: 130,
                renderCell: (params) => (
                    params.value ? (
                        <Chip
                            label={params.value}
                            sx={{
                                borderColor: stringToColor(params.value),
                                color: stringToColor(params.value),
                                fontWeight: 'bold'
                            }}
                            variant="outlined"
                            size="small"
                        />
                    ) : null
                )
            }
        ];

        const dynamicColumns = tableData.length > 0
            ? Object.keys(tableData[0])
                .filter(key => key !== 'id' && !fixedColumns.some(col => col.field === key))
                .map(key => ({ field: key, headerName: key, width: 150 }))
            : [];

        return [...fixedColumns, ...dynamicColumns];
    }, [tableData]);

    return (
        <Box sx={{ p: 2, height: '100vh', width: '100vw', bgcolor: '#f5f5f5' }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight="bold">Visualização da Tabela</Typography>
                <Typography variant="body2" color="text.secondary">Total de registros: {tableData.length}</Typography>
            </Box>
            <Box sx={{ height: 'calc(100vh - 80px)', width: '100%', bgcolor: 'white', borderRadius: 2, overflow: 'hidden', boxShadow: 3 }}>
                <DataGrid
                    rows={tableData}
                    columns={columns}
                    getRowClassName={(params) => getRowClassName(params.row)}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 100, page: 0 },
                        },
                    }}
                    pageSizeOptions={[100, 500, { value: tableData.length, label: 'Todos' }]}
                    components={{ Toolbar: GridToolbar }}
                    sx={{
                        border: 0,
                        '& .Conciliado': { bgcolor: '#f1f8e9' },
                        '& .Não-Conciliado': { bgcolor: '#fff5f5' },
                        '& .Parcialmente-Conciliado': { bgcolor: '#fffde7' },
                    }}
                />
            </Box>
        </Box>
    );
};

export default TabelaPreview;
